export interface SessionListItem {
  _id: string;
  userId: string;
  subject: string;
  topic?: string;
  createdAt: string;
  updatedAt: string;
  messageCount?: number;
  lastMessage?: string;
  sessionDate?: string;
  sessionTime?: string;
  formattedDateTime?: string;
}

export interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface SessionListResponse {
  success: boolean;
  data: SessionListItem[];
  pagination: PaginationInfo;
  filters: {
    startDate: string | null;
    endDate: string | null;
    subject?: string;
  };
  message?: string;
}

export const getLatestSessions = async (
  limit: number = 10, 
  startDate?: string, 
  endDate?: string,
  page: number = 1,
  offset?: number,
  subject?: string
): Promise<SessionListResponse> => {
    try {
     
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found. Please login.');
        throw new Error('Unauthorized: No token found');
      }

      if (!subject || subject.trim().length === 0) {
        throw new Error('Missing required parameter: subject');
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('limit', limit.toString());
      
      if (startDate) {
        queryParams.append('startDate', startDate);
      }
      
      if (endDate) {
        queryParams.append('endDate', endDate);
      }

      queryParams.append('subject', subject.trim());

      if (offset !== undefined) {
        queryParams.append('offset', offset.toString());
      } else {
        queryParams.append('page', page.toString());
      }
  
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/session/list?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
     
      const result = await response.json();
      console.log('Fetched sessions response:', result);
      
      // Validate the response structure
      if (!result || typeof result !== 'object') {
        throw new Error('Invalid response format');
      }
      
      if (!result.success) {
        throw new Error(result.message || 'Request failed');
      }
      
      if (!result.data || !Array.isArray(result.data)) {
        console.error('Unexpected response structure:', result);
        throw new Error('Invalid data format in response');
      }
      
      return result;
  
    } catch (error) {
      console.error('Error fetching latest sessions:', error);
      throw error;
    }
  };


export interface SessionMessagesPagination {
  totalMessages: number;
  limit: number;
  hasMore: boolean;
  nextCursor: string | null;
}

export interface SessionMessagesResponse {
  success: boolean;
  data: {
    _id: string;
    userId: string;
    subject: string;
    topic?: string;
    messages: any[];
    createdAt: string;
    updatedAt: string;
    __v?: number;
  };
  pagination?: SessionMessagesPagination;
}

interface GetSessionMessagesParams {
  limit?: number;
  before?: string | null;
}

export const getSessionMessages = async (
  sessionId: string,
  params?: GetSessionMessagesParams
): Promise<SessionMessagesResponse> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found. Please login.');
      throw new Error('Unauthorized: No token found');
    }

    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.before) {
      queryParams.append('before', params.before);
    }

    const queryString = queryParams.toString();
    const url = `${process.env.REACT_APP_API_URL}/api/session/${sessionId}/messages${
      queryString ? `?${queryString}` : ''
    }`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching session messages:', error);
    throw error;
  }
};
